import type { Row } from "@tanstack/react-table";
import _, { isEmpty } from "lodash";
import {
  EllipsisVertical,
  HelpCircle,
  ListCollapse,
  ListTree,
  Search,
} from "lucide-react";
import { useQueryState } from "nuqs";
import React, { useMemo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Supplier } from "@/apis/gql/types";

import {
  InvoiceRowData,
  Table,
  TablePagination,
  useTableInstanceContext,
} from "@/components/shared/data-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { TableBodyRow } from "@/components/ui/virtualized-table";
import { AskAi } from "@/features/ask-ai/ask-ai";
import { supplierResearchPrompt } from "@/lib/prompts/supplier-research";
import { useGetAllPastBookings } from "@/queries/past-bookings/get-all";
import type { ImperativePanelHandle } from "react-resizable-panels";

import { UsageHelperBanner } from "@/components/shared/banners/usage-helper-banner";
import { NoRecordsEmpty } from "@/components/shared/data-table/empty-message";
import { Count } from "@/components/shared/data-table/total-count";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { ActionBar } from "./action-bar";
import { Filters } from "./filters";
import { InfoPanel, InfoPanelTab } from "./info-panel";
import { InvoiceRow } from "./invoice-row";

type Props = {
  loading: boolean;
  onSupplierDone: (
    ids: string[],
    supplierId: string,
    onUndo?: () => void,
  ) => void;
  onNotABooking: (invoiceId: string, lineItemIds: string[]) => void;
  suppliers: Supplier[];
  supplierId: string;
  onSupplierChange: (supplierId: string | null, origin: string) => void;
  error?: Error | string;
  invoiceType?: InvoiceTypeString;
};

export const DataTable = ({
  loading,
  error,
  suppliers,
  supplierId,
  onSupplierDone,
  onNotABooking,
  onSupplierChange,
  invoiceType,
}: Props) => {
  const [height, setHeight] = React.useState(0);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const { table, focusedRowId } = useTableInstanceContext();
  const [infoPanelOpened, setInfoPanelOpened] = useQueryState("info_panel", {
    defaultValue: false,
    parse: (value) => value === "true",
    serialize: (value) => String(value),
  });
  const [currentTab, setCurrentTab] = useQueryState<InfoPanelTab>("tab", {
    defaultValue: InfoPanelTab.INVOICE,
    parse: (value) => {
      if (value === InfoPanelTab.PAST_BOOKINGS)
        return InfoPanelTab.PAST_BOOKINGS;
      if (value === InfoPanelTab.COMMENTS) return InfoPanelTab.COMMENTS;
      return InfoPanelTab.INVOICE;
    },
  });
  const { t } = useTranslation();
  const [containerWidth, setContainerWidth] = useState(0);

  const _ref = React.useRef(null);
  const rightPanelRef = React.useRef<ImperativePanelHandle>(null);

  const config = useMemo(() => {
    return {
      suppliers,
    };
  }, [suppliers]);

  useEffect(() => {
    const panel = rightPanelRef.current;
    if (!panel) return;
    if (infoPanelOpened) {
      panel.expand();
    } else {
      panel.collapse();
    }
  }, [infoPanelOpened]);

  useEffect(() => {
    const node = _ref.current;

    if (!node) return;

    const resizeObserver = new ResizeObserver(([e]) => {
      const _height = e.contentRect.height;
      !height && setHeight(_height);
    });

    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, []);

  const currentSupplier = useMemo(() => {
    return config.suppliers.find((supplier: Supplier) => {
      let _supplierId: string | null;

      if (supplierId === "unknown") {
        _supplierId = null;
      } else {
        _supplierId = supplierId;
      }

      return _supplierId === supplier.supplierId;
    });
  }, [config.suppliers, supplierId]);

  const handleSetSupplier = useCallback(
    (supplierId: string) => {
      onSupplierChange(supplierId, "set");
    },
    [onSupplierChange],
  );

  const handleNextSupplier = useCallback(() => {
    const suppliers = config?.suppliers ?? [];

    if (suppliers.length === 1) {
      onSupplierChange(null, "next");
      return;
    }

    let currentSupplierIndex = suppliers.findIndex(
      (supplier: Supplier) =>
        supplier.supplierId === currentSupplier?.supplierId,
    );

    if (currentSupplierIndex === suppliers.length - 1) {
      currentSupplierIndex = -1;
    }

    const nextSupplier = suppliers[currentSupplierIndex + 1];

    if (nextSupplier) {
      // biome-ignore lint/style/noNonNullAssertion: supplierId is always set
      onSupplierChange(nextSupplier.supplierId!, "next");
    }
  }, [config, currentSupplier, onSupplierChange]);

  const handlePreviousSupplier = useCallback(() => {
    const suppliers = config?.suppliers ?? [];

    if (suppliers.length === 1) {
      onSupplierChange(null, "previous");
      return;
    }

    let currentSupplierIndex = suppliers.findIndex(
      (supplier: Supplier) =>
        supplier.supplierId === currentSupplier?.supplierId,
    );

    if (currentSupplierIndex === 0) {
      currentSupplierIndex = suppliers.length;
    }

    const previousSupplier = suppliers[currentSupplierIndex - 1];

    if (previousSupplier) {
      // biome-ignore lint/style/noNonNullAssertion: supplierId is always set
      onSupplierChange(previousSupplier.supplierId!, "previous");
    }
  }, [config, currentSupplier, onSupplierChange]);

  useEffect(() => {
    if (focusedRowId) {
      setInfoPanelOpened(true);
      setCurrentTab(InfoPanelTab.INVOICE);
    }
  }, [focusedRowId]);

  const infoPanelData = useMemo(() => {
    if (!focusedRowId)
      return {
        supplier: currentSupplier,
      };

    let currentRow = null;

    try {
      currentRow = table.getRow(focusedRowId);
    } catch (error) {
      currentRow = null;
    }

    if (!currentRow)
      return {
        supplier: currentSupplier,
      };

    const supplier = currentRow.original.supplier;

    return {
      invoiceId: currentRow.original.invoiceId,
      lineItemId: currentRow.original.id,
      accountId: currentRow.original.accountId,
      supplier: supplier ?? {},
    };
  }, [focusedRowId, table.getRow, currentSupplier]);

  const rows = table.getRowModel().rows;

  // Automatically focus the first invoice row when the supplier changes while the info panel is open
  useEffect(() => {
    // Only run when the info panel is NOT open or we are still loading data
    if (!infoPanelOpened || loading) return;

    const focusedRowId = table.getFocusedRowId?.();

    // Check if the currently focused row still exists in the freshly loaded rows
    const stillExists =
      focusedRowId && rows.some((row) => row.id === focusedRowId);

    // If the focused row no longer exists (i.e. supplier changed) focus the first top-level invoice row
    if (!stillExists) {
      const firstInvoiceRow = rows.find((row) => row.depth === 0);
      if (firstInvoiceRow) {
        table.setFocusedRow(firstInvoiceRow.id);
      }
    }
  }, [supplierId, infoPanelOpened, loading, rows]);

  const handleSupplierDone = useCallback(async () => {
    const onUndo = async () => {
      if (currentSupplier) {
        // biome-ignore lint/style/noNonNullAssertion: supplierId is always set
        onSupplierChange(currentSupplier.supplierId!, "done");
      }
    };

    const lineItemIds = rows.reduce((acc: string[], row) => {
      const isBookingGroup = row.depth === 1;

      if (isBookingGroup) {
        row.original.subRows.forEach((subRow: any) => {
          acc.push(subRow.id);
        });
      }
      return acc;
    }, []);

    onSupplierDone(lineItemIds, currentSupplier?.supplierId ?? "", onUndo);

    handleNextSupplier();
  }, [rows, onSupplierDone, currentSupplier, config]);

  const { data: invoices, loading: invoicesLoading } = useGetAllPastBookings({
    supplierId: currentSupplier?.supplierId ?? undefined,
  });

  const toggleExpandAll = (expanded: boolean) => {
    // @ts-ignore TODO: I don't want to fix this right now, skip expand one sub row prop should be overridden by the table options
    table.toggleAllRowsExpanded(expanded, true);
  };

  const handleOpenHistoricalBookings = () => {
    setInfoPanelOpened(true);
    setCurrentTab(InfoPanelTab.PAST_BOOKINGS);
  };

  const handleNotABooking = (invoiceId: string, lineItemIds: string[]) => {
    onNotABooking(invoiceId, lineItemIds);
  };

  const debounceTimerRef = React.useRef<NodeJS.Timeout>();

  useEffect(() => {
    const rows = table.getRowModel().rows;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (loading) return;

    if (supplierId) {
      debounceTimerRef.current = setTimeout(() => {
        if (rows.length === 0 && !loading && suppliers.length > 1) {
          handleNextSupplier();
        }
      }, 500);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [supplierId, handleNextSupplier, loading, rows.length]);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;

    const update = () => setContainerWidth(el.clientWidth - 16);
    update();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    } else {
      window.addEventListener("resize", update);
    }

    return () => {
      if (ro) {
        ro.disconnect();
      } else {
        window.removeEventListener("resize", update);
      }
    };
  }, [tableContainerRef]);

  return (
    <div ref={_ref} className="h-full pb-2 gap-4">
      <ResizablePanelGroup
        direction="horizontal"
        autoSaveId="fastlane-info-panel"
        className="h-full gap-2"
      >
        <ResizablePanel minSize={35} defaultSize={infoPanelOpened ? 65 : 100}>
          <div className="flex flex-row h-full" ref={tableContainerRef}>
            <div className="w-full h-full flex flex-col">
              <div className="flex flex-col gap-4 flex-1">
                <UsageHelperBanner
                  label={
                    invoiceType === "receivable"
                      ? t("todos.receivable.fastlane_explainer")
                      : t("todos.payable.fastlane_explainer")
                  }
                  link="https://tabula-accounting.notion.site/Aufgaben-Schritt-4-Fastlane-27beecb29d04809aa4c2d92462c441dc?pvs=74"
                  linkText={t("fastlane.how_it_works")}
                />
                <div className="flex flex-col gap-2">
                  <div className="flex flex-row gap-2 items-center flex-wrap justify-between">
                    <div className="flex flex-row gap-2 items-center flex-wrap">
                      <Filters
                        config={config}
                        loading={loading}
                        values={{
                          supplierId:
                            currentSupplier?.supplierId === null
                              ? "unknown"
                              : currentSupplier?.supplierId ?? "",
                        }}
                        onSupplierChange={onSupplierChange}
                        invoiceType={invoiceType}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            className="flex h-10 w-10 p-0 data-[state=open]:bg-muted"
                          >
                            <EllipsisVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <AskAi
                            prompt={supplierResearchPrompt(
                              currentSupplier?.supplierName ?? "",
                              currentSupplier?.address ?? undefined,
                            )}
                            query={currentSupplier?.supplierName ?? ""}
                            type="wordware"
                          >
                            {({ askAssistant }) => (
                              <DropdownMenuItem
                                className="gap-2"
                                onSelect={askAssistant}
                              >
                                <Search className="h-4 w-4" />
                                <span>
                                  {invoiceType === "receivable"
                                    ? t("ask_ai.extras.debtors")
                                    : t("ask_ai.extras.creditors")}
                                </span>
                              </DropdownMenuItem>
                            )}
                          </AskAi>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
                <Table
                  loading={loading}
                  error={error}
                  emptyStateComponent={<NoRecordsEmpty state="done" />}
                >
                  {({
                    rows,
                    rowVirtualizer,
                    table,
                    clickableColumns,
                    onRowClick,
                  }) => {
                    return rowVirtualizer
                      .getVirtualItems()
                      .map((virtualRow: any) => {
                        const row = rows[virtualRow.index] as Row<any>;

                        if (row.depth === 0) {
                          return (
                            <InvoiceRow
                              row={row}
                              table={table}
                              virtualRow={virtualRow}
                              rowVirtualizer={rowVirtualizer}
                              onNotABooking={handleNotABooking}
                              setInfoPanelOpened={setInfoPanelOpened}
                              tableContainerWidth={containerWidth}
                            />
                          );
                        }

                        return (
                          <TableBodyRow
                            key={row.id}
                            row={row}
                            virtualRow={virtualRow}
                            rowVirtualizer={rowVirtualizer}
                            table={table}
                            clickableColumns={clickableColumns}
                            onRowClick={onRowClick}
                          />
                        );
                      });
                  }}
                </Table>
                <TablePagination disablePagination />
                <ActionBar
                  onNextSupplier={handleNextSupplier}
                  onPreviousSupplier={handlePreviousSupplier}
                  onSupplierDone={handleSupplierDone}
                  disableSupplierNavigation={isEmpty(currentSupplier)}
                  invoiceType={invoiceType}
                  onSetSupplier={handleSetSupplier}
                />
              </div>
            </div>
          </div>
        </ResizablePanel>

        {infoPanelOpened && (
          <ResizableHandle
            withHandle
            className="bg-transparent after:bg-transparent hover:[&>div]:bg-border/2 mr-[-9px]"
          />
        )}

        <ResizablePanel
          ref={rightPanelRef}
          minSize={30}
          defaultSize={35}
          collapsible
          collapsedSize={infoPanelOpened ? 30 : 0}
          style={{
            minWidth: infoPanelOpened ? "520px" : "0px",
          }}
        >
          <div className="h-full flex flex-row">
            <div className="h-full w-full rounded-md border">
              <InfoPanel
                invoiceId={infoPanelData.invoiceId}
                accountId={infoPanelData.accountId}
                lineItemId={infoPanelData.lineItemId}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                supplier={{
                  id: infoPanelData.supplier?.parentSupplierId
                    ? infoPanelData.supplier?.parentSupplierId
                    : infoPanelData.supplier?.supplierId ?? "",
                  name: infoPanelData.supplier?.supplierName ?? "",
                  no: infoPanelData.supplier?.datevAccountNumber ?? "",
                }}
              />
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};
